import { QUEUE_COUNTS_KEY, fetchQueueCounts } from '@/lib/queueCounts';
import { useQuery } from '@tanstack/react-query';
import type { OrderQueue } from '@elutide/shared';
import { LIVE } from '@/app/queryClient';
import { useMetrics } from '@/lib/metrics';
import { useSession } from '@/app/session';
import { formatMetricValue } from '@/lib/format';
import { Card, ErrorState, KpiTile, Skeleton } from '@/components';

const MONEY_IDS = ['revenue_net', 'orders_paid', 'aov'];
const COUNT_IDS = ['orders_paid'];

/**
 * One window for every tile in this card.
 *
 * `today` resolves on the server to local midnight America/New_York through now, DST
 * included (packages/metrics/src/window.ts). "Paid today" and "Orders paid" are read in the
 * same batch at this window, so they are two views of one set of paid orders and cannot
 * disagree about which orders count.
 */
const TODAY_WINDOW = 'today' as const;

/**
 * Five live tiles. The money tile is simply absent for a packer, because the server omits a
 * money metric for that role rather than sending a zero.
 *
 * The queue numbers here come from `/orders/queues`, the same call the Orders rail makes, so
 * "awaiting payment 6" on Home and in Orders can never disagree.
 */
export function TodayTiles() {
  const { can } = useSession();
  const showMoney = can('metrics.read_money');

  const metrics = useMetrics(
    'home-today',
    showMoney ? MONEY_IDS : COUNT_IDS,
    TODAY_WINDOW,
  );

  const queues = useQuery({
    queryKey: QUEUE_COUNTS_KEY,
    queryFn: fetchQueueCounts,
    ...LIVE,
  });

  const revenue = metrics.get('revenue_net');
  const ordersPaid = metrics.get('orders_paid');
  const aov = metrics.get('aov');

  const queueEnvelope = (queue: OrderQueue) => {
    const count = queues.data?.[queue];
    if (count === undefined || !ordersPaid) return null;
    // Queue counts are point in time and carry the same envelope shape as any other number,
    // so the tile renders a basis and an age rather than a bare integer.
    return {
      metric_id: 'queue_count',
      version: 2,
      value: count,
      unit: 'count' as const,
      basis: 'woo_created' as const,
      window: {
        kind: 'point' as const,
        start: null,
        end: null,
        tz: 'America/New_York' as const,
        label: 'now',
      },
      as_of: ordersPaid.as_of,
      sources: ['order_ops_state', 'order_flags'],
      dims: { queue },
      partial: false,
      coverage: null,
    };
  };

  if (metrics.isLoading && queues.isLoading) {
    return (
      <Card title="Today">
        <Skeleton shape="card" lines={2} label="Loading today" />
      </Card>
    );
  }

  return (
    <Card
      title="Today"
      asOf={metrics.asOf}
      ageClass="live"
      error={metrics.error ? 'Today did not refresh. These are the last known numbers.' : null}
      onRetry={metrics.refetch}
      id="today"
    >
      {queues.isError && metrics.error ? (
        <ErrorState message="Neither the metrics nor the queue counts answered." onRetry={metrics.refetch} />
      ) : null}
      <div className="o-grid o-grid--tiles">
        {showMoney ? (
          <KpiTile label="Paid today" envelope={revenue ?? null} showCents />
        ) : null}
        <KpiTile
          label="Orders paid"
          envelope={ordersPaid ?? null}
          note={aov ? `AOV ${formatMetricValue(aov)}` : null}
        />
        <KpiTile
          label="Awaiting payment"
          envelope={queueEnvelope('awaiting_payment')}
          tone="warn"
        />
        <KpiTile label="To print" envelope={queueEnvelope('to_print')} />
        <KpiTile label="To pack" envelope={queueEnvelope('to_pack')} />
        <KpiTile label="In transit" envelope={queueEnvelope('in_transit')} />
      </div>
    </Card>
  );
}
